import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { sumMoney } from '../lib/display-labels';
import { displayValue } from '../features/fieldiq-handoff/contracts';
import PageHeaderBand, { type PageHeaderStat } from '../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import {
  LOAD_FAILURE_QUERY_OPTIONS,
  queryFailureError,
  queryLoadFailure,
} from '../lib/load-failure';
import { toast } from 'sonner';
import ConfirmModal from '../components/ui/ConfirmModal';
import MaintenanceRecordDialog, {
  fmtDate,
  fmtMoney as fmt,
  MAINTENANCE_TYPES,
  STATUS_OPTIONS,
  type MaintenanceRecord,
} from '../components/maintenance/MaintenanceRecordDialog';
import { Badge } from '../components/ui/badge';
import { formatCalendarDate, tenantTodayDate, toCalendarDateString } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import {
  dueExplanation,
  dueRuleLabel,
  statusTiles,
  totalUnits,
  unitDueText,
  unitLabel,
  unitStatusChip,
} from '../lib/maintenanceSummary';
import type {
  MaintenanceSummaryResponse,
  MaintenanceSummarySort,
  UnitMaintenanceSummaryRow,
} from '../types/equipment';
import {
  Plus,
  Search,
  Loader2,
  Wrench,
  DollarSign,
  Download,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Brain,
  CheckCircle,
  Send,
  RefreshCw,
  CalendarClock,
  FileStack,
  Sigma,
} from 'lucide-react';
import { Tooltip } from '../components/ui/tooltip';

/* ─── Types ───────────────────────────────────────────────────────── */

interface EquipmentUnit {
  id: string;
  unit_number?: string;
  name?: string;
  type?: string;
  status?: string;
}

interface MonthlySummaryUnitBreakdown {
  unit_number: string;
  equipment_id: string;
  total: number;
  parts: number;
  labor: number;
  misc: number;
  count: number;
  mom_delta: number;
}

interface MonthlySummaryFlaggedUnit {
  unit_number: string;
  total: number;
  reason: string;
  ai_explanation: string;
}

interface MonthlySummary {
  id: string;
  period_month: string;
  status: 'draft' | 'reviewed' | 'published';
  total_spend: number;
  record_count: number;
  units_serviced: number;
  fleet_median_spend: number;
  unit_breakdown: MonthlySummaryUnitBreakdown[];
  flagged_units: MonthlySummaryFlaggedUnit[];
  summary_text: string | null;
  watch_items: string[];
  generated_at: string;
}

/* ─── Helpers ─────────────────────────────────────────────────────── */

/** Return YYYY-MM for the previous month relative to a given YYYY-MM */
function prevMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 2, 1); // month is 0-indexed, so m-2 goes back one month
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function nextMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** 'September 2026' for a YYYY-MM; an em dash when there is no month. */
function formatMonth(ym: string): string {
  return formatCalendarDate(ym ? `${ym}-01` : null, { month: 'long', year: 'numeric' }) ?? '—';
}

/**
 * The previous calendar month in YYYY-MM, counted from the TENANT's today
 * (`companies.time_zone`), not the device's. Built from day 1 so the 31st of a
 * month cannot roll over into the same month. '' when the zone is unknown —
 * there is no honest "last month" without one.
 */
function defaultMonth(timeZone: string | null): string {
  const today = tenantTodayDate(timeZone);
  if (!today) return '';
  const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * SATURATED STATUS BADGES — a `--fi-signal-*` FILL with `text-fi-signal-ink` on
 * it, per the register idiom.
 *
 * These were pale 30%-wash pills with the hue as the INK: `text-green-400` on
 * `bg-green-900/30`. Neither class is remapped by theme.css, so in light mode a
 * bright dark-mode hue sat on a nearly-white wash on a white panel — the
 * failure CLAUDE.md measures at 1.49/2.08/1.52 for amber/red/green. Nothing
 * reports it: the class is in the DOM, the build is clean, the word is simply
 * not there. A signal hue has no light twin by design, so as a FILL it holds in
 * both halves and one dark ink reads on all of them.
 *
 * FIVE HUES, NO SIXTH IS ADDED. `draft` and an unknown status share slate, which
 * is the same choice EquipmentScreen's map makes for the same reason. An
 * unmapped status keeps its own text on a slate pill — never a guessed label,
 * and never a colour that would assert urgency this build cannot know.
 */
const BADGE_PILL =
  'inline-flex items-center rounded-fi-pill border border-black/20 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-fi-signal-ink';

const summaryStatusBadge = (status: string) => {
  const map: Record<string, { bg: string; label: string }> = {
    draft: { bg: 'bg-fi-signal-slate', label: 'Draft' },
    reviewed: { bg: 'bg-fi-signal-blue', label: 'Reviewed' },
    published: { bg: 'bg-fi-signal-green', label: 'Published' },
  };
  const s = map[status] || { bg: 'bg-fi-signal-slate', label: status };
  return <span className={`${BADGE_PILL} ${s.bg}`}>{s.label}</span>;
};

const statusBadge = (status: string) => {
  const map: Record<string, { bg: string; label: string }> = {
    completed: { bg: 'bg-fi-signal-green', label: 'Completed' },
    scheduled: { bg: 'bg-fi-signal-blue', label: 'Scheduled' },
    in_progress: { bg: 'bg-fi-signal-amber', label: 'In Progress' },
    cancelled: { bg: 'bg-fi-signal-red', label: 'Cancelled' },
  };
  const s = map[status] || { bg: 'bg-fi-signal-slate', label: status };
  return <span className={`${BADGE_PILL} ${s.bg}`}>{s.label}</span>;
};

/**
 * The default record range: the last 90 days, ending on the TENANT's today.
 * It used to be computed once at module load from the device clock, so it read
 * the device's day and went stale in a long session. '' (an empty picker) when
 * the zone is unknown — an input seed, never a rendered value.
 */
function defaultRange(timeZone: string | null): { start: string; end: string } {
  const today = tenantTodayDate(timeZone);
  if (!today) return { start: '', end: '' };
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 90);
  return { start: toCalendarDateString(start), end: toCalendarDateString(today) };
}

/* ─── Main Screen ─────────────────────────────────────────────────── */

export default function EquipmentMaintenanceReportScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const timeZone = useTenantTimeZone();

  // Filters
  const [filterUnit, setFilterUnit] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [startDate, setStartDate] = useState(() => defaultRange(timeZone).start);
  const [endDate, setEndDate] = useState(() => defaultRange(timeZone).end);
  const [search, setSearch] = useState('');

  // Per-unit roll-up controls. `dueStatus` is the unit's due state — distinct
  // from `filterStatus`, which is a maintenance RECORD's workflow status.
  const [dueStatus, setDueStatus] = useState('');
  const [summarySort, setSummarySort] = useState<MaintenanceSummarySort>('due');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<MaintenanceRecord | null>(null);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<MaintenanceRecord | null>(null);

  // Monthly cost analysis state
  const [selectedMonth, setSelectedMonth] = useState(() => defaultMonth(timeZone));
  const selectedYear = parseInt(selectedMonth.split('-')[0], 10);
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false);

  /* ── Monthly Summary Queries ── */

  /**
   * `LOAD_FAILURE_QUERY_OPTIONS` on all four reads, and every query object is
   * kept whole rather than destructured to `{ data, isLoading }`.
   *
   * A two-branch destructure is what every empty state on this page was built
   * on, and each of them made a claim the screen had no basis for: "No units on
   * file.", "No equipment on file.", "No maintenance records found." and — the
   * worst — "No summary exists for <month>." over a **Generate Summary**
   * button, which offers a WRITE as the remedy for a failed READ (the
   * QuotesScreen shape, fe#573).
   *
   * React Query's default `networkMode: 'online'` also parks a retrying failure
   * in `paused`, where `error` is null, `data` is undefined and `isLoading` is
   * FALSE — so `data = []` and the reassuring branch renders. A screen checked
   * only against a 403 looks completely correct and is unfixed for every 5xx.
   * `queryLoadFailure` reads the failure wherever the query is keeping it.
   */
  const monthlySummariesQuery = useQuery<MonthlySummary[]>({
    queryKey: ['equipment-maintenance', 'summaries', selectedYear],
    queryFn: () => api.equipmentMaintenance.summaries.list(selectedYear).then((r) => r.data?.data ?? r.data ?? []),
    // No month without a tenant zone (see defaultMonth); never ask for year NaN.
    enabled: Number.isFinite(selectedYear),
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data: monthlySummaries = [], isLoading: monthlySummariesLoading } = monthlySummariesQuery;
  const monthlySummariesFailure = queryLoadFailure(monthlySummariesQuery);

  const currentSummary = useMemo(
    () => (monthlySummaries as MonthlySummary[]).find((s) => s.period_month?.slice(0, 7) === selectedMonth) ?? null,
    [monthlySummaries, selectedMonth],
  );

  const generateMutation = useMutation({
    mutationFn: (data: { month: string; force?: boolean }) => api.equipmentMaintenance.summaries.generate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-maintenance', 'summaries'] });
      toast.success('Summary generated');
      setRegenerateConfirmOpen(false);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to generate summary');
      setRegenerateConfirmOpen(false);
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'draft' | 'reviewed' | 'published' }) =>
      api.equipmentMaintenance.summaries.updateStatus(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-maintenance', 'summaries'] });
      toast.success('Status updated');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to update status');
    },
  });

  /* ── Queries ── */

  /**
   * The summary endpoint returns `as_of` and `counts` alongside `data`. Falls
   * back to reading a bare array so a lagging API deploy renders rows instead of
   * a blank screen.
   */
  const fetchSummary = useCallback(
    (params: Record<string, string>): Promise<MaintenanceSummaryResponse> =>
      api.equipmentMaintenance.summary(params).then((r) => {
        const body: any = r.data ?? {};
        const rows: UnitMaintenanceSummaryRow[] = Array.isArray(body?.data)
          ? body.data
          : Array.isArray(body)
            ? body
            : [];
        return {
          as_of: body?.as_of ?? null,
          counts: body?.counts ?? {},
          data: rows,
        };
      }),
    [],
  );

  // Two reads, one shape. The fleet read is never narrowed by ?status=, so the
  // count tiles and the spend KPIs keep describing the whole fleet while the
  // table below narrows. With no status filter both keys hash identically and
  // React Query issues a single request.
  const fleetParams = useMemo<Record<string, string>>(() => ({ sort: summarySort }), [summarySort]);
  const summaryParams = useMemo<Record<string, string>>(() => {
    const p: Record<string, string> = { sort: summarySort };
    if (dueStatus) p.status = dueStatus;
    return p;
  }, [summarySort, dueStatus]);

  const fleetSummaryQuery = useQuery({
    queryKey: ['equipment-maintenance', 'summary', fleetParams],
    queryFn: () => fetchSummary(fleetParams),
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data: fleetSummary, isLoading: summaryLoading } = fleetSummaryQuery;
  const fleetFailure = queryLoadFailure(fleetSummaryQuery);

  const unitSummaryQuery = useQuery({
    queryKey: ['equipment-maintenance', 'summary', summaryParams],
    queryFn: () => fetchSummary(summaryParams),
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data: unitSummary, isLoading: unitSummaryLoading } = unitSummaryQuery;
  const unitFailure = queryLoadFailure(unitSummaryQuery);

  const fleetRows: UnitMaintenanceSummaryRow[] = useMemo(() => fleetSummary?.data ?? [], [fleetSummary]);
  const unitRows: UnitMaintenanceSummaryRow[] = useMemo(() => unitSummary?.data ?? [], [unitSummary]);
  const tiles = useMemo(() => statusTiles(fleetSummary?.counts), [fleetSummary]);
  const fleetTotal = totalUnits(fleetSummary?.counts) ?? fleetRows.length;

  const listParams = useMemo(() => {
    const p: Record<string, string> = {};
    if (filterUnit) p.equipment_id = filterUnit;
    if (filterType) p.maintenance_type = filterType;
    if (filterStatus) p.status = filterStatus;
    if (startDate) p.start_date = startDate;
    if (endDate) p.end_date = endDate;
    return p;
  }, [filterUnit, filterType, filterStatus, startDate, endDate]);

  const recordsQuery = useQuery({
    queryKey: ['equipment-maintenance', 'list', listParams],
    queryFn: () => api.equipmentMaintenance.list(listParams).then((r) => r.data?.data ?? r.data ?? []),
    staleTime: 15_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data: records = [], isLoading: recordsLoading } = recordsQuery;
  const recordsFailure = queryLoadFailure(recordsQuery);

  const { data: equipmentList = [] } = useQuery<EquipmentUnit[]>({
    queryKey: ['equipment'],
    queryFn: () => api.equipment.list().then((r) => r.data?.data ?? r.data ?? []),
    staleTime: 60_000,
  });

  /* ── Filtered + searched records ── */

  const filteredRecords = useMemo(() => {
    if (!search) return records as MaintenanceRecord[];
    const q = search.toLowerCase();
    return (records as MaintenanceRecord[]).filter((r) =>
      [r.unit_number, r.description, r.performed_by, r.vendor, r.maintenance_type]
        .some((v) => v?.toLowerCase().includes(q)),
    );
  }, [records, search]);

  /* ── Aggregated summary KPIs ── */

  // Fleet-wide, deliberately unaffected by the due-status filter below.
  const totalSpend = useMemo(
    () => sumMoney(fleetRows.map((u) => u.total_spend)),
    [fleetRows],
  );
  const totalRecords = useMemo(
    () => fleetRows.reduce((s, u) => s + (Number(u.record_count) || 0), 0),
    [fleetRows],
  );
  const unitsWithRecords = useMemo(
    () => fleetRows.filter((u) => Number(u.record_count) > 0).length,
    [fleetRows],
  );

  /**
   * THE FOUR SPEND FIGURES ARE THE BAND. The seven fleet-due tiles are a
   * FILTER, so they became chips in the body (see the chip row below).
   *
   * All four come off the fleet read, which is never narrowed by the chips, so
   * the band keeps describing the whole fleet while the register under it
   * narrows — the split this screen already made, now visible in the layout.
   *
   * ORDER IS THE CIRCLE'S, NOT THE NARRATIVE'S. The third slot's label has a
   * hard ~70px ceiling (CLAUDE.md, fe#698): between 1024 and 1048 wide the hero
   * circle's 28%-amber edge crosses the third column, and a 96px label read
   * 3.88:1 there. "Units Serviced" measures 107.7px in this slot's type, so it
   * takes the unconstrained second slot and "Records" (57.7px) is third.
   * Measured in a browser at every 8px from 1024 to 2560, in both themes — see
   * the PR body; the labels were not chosen by eye.
   *
   * `sumMoney` RETURNS NULL IF ANY ROW'S SPEND IS ABSENT, and `fmt` is
   * fmtCurrency, so the band renders an em dash rather than a total that
   * silently excludes the rows it could not read. Avg per Unit divides only
   * when both halves are real; zero serviced units is an absent average, not
   * $0.00.
   */
  const bandStats = useMemo((): PageHeaderStat[] => [
    {
      icon: <DollarSign className="size-4" />,
      value: fmt(totalSpend),
      label: 'Total Spend',
      sub: 'Fleet-wide',
      tone: 'accent',
      loading: summaryLoading,
    },
    {
      icon: <Wrench className="size-4" />,
      value: displayValue(unitsWithRecords),
      /* "Serviced", NOT "Units Serviced", AND THE REASON IS TRUNCATION RATHER
         THAN CONTRAST. The band's stat label carries `truncate`, and at 1024 a
         four-column strip gives each label about 120px: "Units Serviced"
         rendered as "UNITS SERVIC…" in the 1024 screenshot, which reads as a
         broken word rather than a short one. This is Equipment's own precedent
         (fe#698) — "Job Finished" became "Finished" with the sub-line carrying
         the sense — applied for the neighbouring reason. The register below
         still says Units Serviced in full. */
      label: 'Serviced',
      sub: 'Units with a record',
      tone: 'info',
      loading: summaryLoading,
    },
    {
      icon: <FileStack className="size-4" />,
      value: displayValue(totalRecords),
      label: 'Records',
      sub: 'On the fleet',
      loading: summaryLoading,
    },
    {
      icon: <Sigma className="size-4" />,
      value: fmt(totalSpend != null && unitsWithRecords ? totalSpend / unitsWithRecords : null),
      label: 'Avg / Unit',
      sub: 'Per serviced unit',
      tone: 'success',
      loading: summaryLoading,
    },
  ], [totalSpend, unitsWithRecords, totalRecords, summaryLoading]);

  /* ── Mutations ── */

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.equipmentMaintenance.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-maintenance'] });
      toast.success('Record deleted');
      setDeleteTarget(null);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to delete record');
    },
  });

  /* ── Dialog helpers ── */

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingRecord(null);
  }, []);

  const openAdd = useCallback(() => {
    setEditingRecord(null);
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((rec: MaintenanceRecord) => {
    setEditingRecord(rec);
    setDialogOpen(true);
  }, []);

  /* ── PDF Downloads ── */

  const [downloadingRecordPdf, setDownloadingRecordPdf] = useState<string | null>(null);
  const [downloadingHistoryPdf, setDownloadingHistoryPdf] = useState(false);

  const downloadBlob = useCallback((blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const handleDownloadRecordPdf = useCallback(async (id: string, unitNumber?: string) => {
    setDownloadingRecordPdf(id);
    try {
      const res = await api.equipmentMaintenance.recordPdf(id);
      downloadBlob(new Blob([res.data], { type: 'application/pdf' }), `maintenance-${unitNumber || id.slice(0, 8)}.pdf`);
    } catch (err) {
      console.error('PDF download error:', err);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingRecordPdf(null);
    }
  }, [downloadBlob]);

  const handleDownloadHistoryPdf = useCallback(async () => {
    setDownloadingHistoryPdf(true);
    try {
      const p: Record<string, string> = {};
      if (filterUnit) {
        const unit = (equipmentList as EquipmentUnit[]).find((e) => e.id === filterUnit);
        if (unit) p.unit_number = unit.unit_number || unit.name || '';
        p.equipment_id = filterUnit;
      }
      if (filterType) p.maintenance_type = filterType;
      if (filterStatus) p.status = filterStatus;
      if (startDate) p.from = startDate;
      if (endDate) p.to = endDate;
      const res = await api.equipmentMaintenance.historyPdf(p);
      downloadBlob(new Blob([res.data], { type: 'application/pdf' }), 'maintenance-history.pdf');
    } catch (err) {
      console.error('History PDF download error:', err);
      toast.error('Failed to download history PDF');
    } finally {
      setDownloadingHistoryPdf(false);
    }
  }, [filterUnit, filterType, filterStatus, startDate, endDate, equipmentList, downloadBlob]);

  /* ── Render ─────────────────────────────────────────────────────── */

  return (
    <div data-testid="equipment-maintenance-report-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* The app shell renders the one global <Header />. Pages never do. */}
      <PageHeaderBand
        eyebrow="Reports"
        title="Equipment Maintenance"
        // /reports, not navigate(-1) — fe#725 repointed all eleven standalone
        // report pages at the catalog. This page's control was also icon-only
        // with no accessible name at all; the band's `backLabel` is that name.
        onBack={() => navigate('/reports')}
        backLabel="Back to reports"
        /* ONE ACTION ON THE BAND, AND THE SECOND ONE MOVED FOR A MEASURED
           REASON, not for tidiness. With both buttons here the actions block
           squeezed the copy column and "Equipment Maintenance" wrapped to two
           lines at 1024, which took the title row from 56px to 92px and pushed
           the stat strip from 109px below the band's top to 145px — down into
           the widest part of the hero circle, whose clearance was swept against
           Quotes' and Rate Sheets' stat text AT THEIR HEIGHT. The sweep for
           this PR read the fourth sub-line ("Per serviced unit") at **3.55 dark
           / 3.59 light** at 1024–1040. One button keeps the title on one line
           and the strip at 109.

           Download History PDF went to the body, beside the filters — which is
           where it belongs anyway: the PDF is scoped BY those filters, and a
           control that honours a filter reads better next to it than above it.
           The handler, its params and its spinner are unchanged. */
        actions={
          <button
            type="button"
            onClick={openAdd}
            className="fi-action fi-action--standard fi-action--primary"
          >
            <Plus className="mr-1.5 inline size-4" aria-hidden="true" /> Add Record
          </button>
        }
        stats={bandStats}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
        {/* THE SEVEN FLEET-DUE TILES ARE NOW CHIPS, and the chips-vs-tabs test
            (CLAUDE.md, fe#704) is why they are chips and not tabs: every one of
            them narrows the SAME list of units and asks the same question of
            it, so each carries a count. They are still the filter control they
            always were — `dueStatus` is unchanged, and so is the select beside
            the register, which is the same state.

            The buckets are read off the response's `counts`, so a status added
            server-side gets a chip instead of disappearing. */}
        <div>
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="flex items-center gap-1.5 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
              <CalendarClock className="size-3.5 text-fieldiq-accent" aria-hidden="true" /> Fleet Due Status
            </p>
            {fleetSummary?.as_of && (
              <p className="text-[11px] text-fieldiq-muted">As of {fmtDate(fleetSummary.as_of)}</p>
            )}
          </div>
          {summaryLoading ? (
            <span
              role="status"
              aria-label="Loading due status"
              className="block h-8 w-full max-w-md animate-pulse rounded-fi-pill bg-fieldiq-border"
            />
          ) : fleetFailure ? (
            /* AN ERROR IS NOT AN EMPTY STATE. "No units on file." stood here,
               which on a maintenance screen is a claim that nothing is overdue. */
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice
                error={queryFailureError(fleetSummaryQuery)}
                kind={fleetFailure}
                subject="the fleet's due status"
              />
            </div>
          ) : tiles.length === 0 ? (
            <p className="py-2 text-sm text-fieldiq-muted">No units on file.</p>
          ) : (
            <div className="scrollbar-hide flex gap-2 overflow-x-auto" role="group" aria-label="Filter units by due status">
              <button
                type="button"
                data-testid="due-status-chip"
                data-due-status=""
                onClick={() => setDueStatus('')}
                aria-pressed={dueStatus === ''}
                className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                  dueStatus === ''
                    ? 'bg-fi-amber text-fi-amber-ink'
                    : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                }`}
              >
                All Units
                <span className={`tabular-nums ${dueStatus === '' ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>
                  {displayValue(fleetTotal)}
                </span>
              </button>
              {tiles.map((tile) => {
                const active = dueStatus === tile.status;
                return (
                  <button
                    key={tile.status}
                    type="button"
                    data-testid="due-status-chip"
                    data-due-status={tile.status}
                    onClick={() => setDueStatus(active ? '' : tile.status)}
                    aria-pressed={active}
                    className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                      active
                        ? 'bg-fi-amber text-fi-amber-ink'
                        : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                    }`}
                  >
                    {tile.label}
                    <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>
                      {displayValue(tile.count)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Monthly Cost Analysis (AI) — KEPT AS IT WAS. Every control, every
            mutation and the whole narrative/watch-items/flagged-units body are
            untouched; the panel's header takes the eyebrow-and-h2 shape the
            other panels use, its tokens moved to the `--fieldiq-*` palette, and
            it gained the load-failure branch below. */}
        <section aria-labelledby="maintenance-ai-title" className="overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
          <div className="flex flex-col gap-2 border-b border-fieldiq-border px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Cost analysis</p>
              <h2 id="maintenance-ai-title" className="mt-1 flex items-center gap-2 text-base font-extrabold tracking-tight text-fieldiq-text">
                <Brain className="size-4 text-fieldiq-accent" aria-hidden="true" /> Monthly Cost Analysis
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelectedMonth(prevMonth(selectedMonth))}
                className="p-1.5 rounded hover:bg-fieldiq-dark/50 text-fieldiq-muted hover:text-fieldiq-text transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium text-fieldiq-text min-w-[140px] text-center">
                {formatMonth(selectedMonth)}
              </span>
              <button
                onClick={() => setSelectedMonth(nextMonth(selectedMonth))}
                className="p-1.5 rounded hover:bg-fieldiq-dark/50 text-fieldiq-muted hover:text-fieldiq-text transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="px-4 py-4 space-y-4">
            {monthlySummariesLoading ? (
              <div className="flex items-center justify-center py-8 text-fieldiq-muted">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading summary…
              </div>
            ) : monthlySummariesFailure ? (
              /* THE EMPTY STATE BELOW OFFERS A WRITE AS THE REMEDY FOR A FAILED
                 READ, which is QuotesScreen's fe#573 exactly: "No summary exists
                 for September 2026." over a **Generate Summary** button. On a
                 403 that button is refused too, and on a dead connection it
                 spends an AI call on a month whose summary may already exist.
                 This branch stands in front of it. */
              <LoadFailureNotice
                error={queryFailureError(monthlySummariesQuery)}
                kind={monthlySummariesFailure}
                subject="monthly cost summaries"
              />
            ) : currentSummary ? (
              <>
                {/* KPIs + status */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="grid grid-cols-3 gap-3 flex-1">
                    <div>
                      <p className="text-[11px] font-medium text-fieldiq-muted uppercase tracking-wide">Total Spend</p>
                      <p className="text-lg font-bold text-fieldiq-text">{fmt(currentSummary.total_spend)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-medium text-fieldiq-muted uppercase tracking-wide">Records</p>
                      <p className="text-lg font-bold text-fieldiq-text">{currentSummary.record_count}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-medium text-fieldiq-muted uppercase tracking-wide">Units Serviced</p>
                      <p className="text-lg font-bold text-fieldiq-text">{currentSummary.units_serviced}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {summaryStatusBadge(currentSummary.status)}
                    {currentSummary.status !== 'reviewed' && (
                      <button
                        onClick={() => updateStatusMutation.mutate({ id: currentSummary.id, status: 'reviewed' })}
                        disabled={updateStatusMutation.isPending}
                        className="flex items-center gap-1.5 bg-fieldiq-card border border-fieldiq-border text-fieldiq-text font-medium px-3 py-1.5 rounded-fi-control text-xs hover:bg-fieldiq-dark/50 transition-colors disabled:opacity-50"
                      >
                        <CheckCircle className="w-3.5 h-3.5" /> Mark Reviewed
                      </button>
                    )}
                    {currentSummary.status !== 'published' && (
                      <button
                        onClick={() => updateStatusMutation.mutate({ id: currentSummary.id, status: 'published' })}
                        disabled={updateStatusMutation.isPending}
                        className="flex items-center gap-1.5 bg-fieldiq-card border border-fieldiq-border text-fieldiq-text font-medium px-3 py-1.5 rounded-fi-control text-xs hover:bg-fieldiq-dark/50 transition-colors disabled:opacity-50"
                      >
                        <Send className="w-3.5 h-3.5" /> Publish
                      </button>
                    )}
                    <button
                      onClick={() => setRegenerateConfirmOpen(true)}
                      disabled={generateMutation.isPending}
                      className="flex items-center gap-1.5 bg-fieldiq-card border border-fieldiq-border text-fieldiq-muted font-medium px-3 py-1.5 rounded-fi-control text-xs hover:text-fieldiq-text hover:bg-fieldiq-dark/50 transition-colors disabled:opacity-50"
                    >
                      {generateMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                      Regenerate
                    </button>
                  </div>
                </div>

                {/* AI Narrative */}
                {currentSummary.summary_text ? (
                  <div className="bg-fieldiq-dark/50 rounded-fi-control p-4 text-sm text-fieldiq-text leading-relaxed whitespace-pre-line">
                    {currentSummary.summary_text}
                  </div>
                ) : (
                  <p className="text-xs text-fieldiq-muted italic">AI narrative is unavailable for this summary.</p>
                )}

                {/* Watch items */}
                {currentSummary.watch_items && currentSummary.watch_items.length > 0 && (
                  <div className="bg-fieldiq-dark/50 rounded-fi-control p-4">
                    <p className="text-xs font-semibold text-fieldiq-text uppercase tracking-wide mb-2">Watch Items</p>
                    <ul className="list-disc list-inside text-sm text-fieldiq-text space-y-1">
                      {currentSummary.watch_items.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Flagged units */}
                <div>
                  <p className="text-xs font-semibold text-fieldiq-text uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-fi-signal-amber" /> Flagged Units
                  </p>
                  {currentSummary.flagged_units && currentSummary.flagged_units.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {currentSummary.flagged_units.map((fu, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            // Find the equipment_id for this unit_number from the equipmentList
                            const eq = (equipmentList as EquipmentUnit[]).find(
                              (e) => (e.unit_number || e.name) === fu.unit_number,
                            );
                            if (eq) setFilterUnit(eq.id);
                          }}
                          className="text-left rounded-fi-control border border-fi-signal-amber/30 bg-fi-signal-amber/10 p-3 hover:bg-fi-signal-amber/20 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-semibold text-fieldiq-text">{fu.unit_number}</span>
                            <span className="text-sm font-bold text-fieldiq-text">{fmt(fu.total)}</span>
                          </div>
                          <p className="mb-1 text-xs font-semibold text-fieldiq-text">{fu.reason}</p>
                          {/* THE THEME'S OWN INK, NOT THE MUTED ONE, AND THE
                              SWEEP IS WHY. The card's wash moved from a dark
                              amber-900 tint to the handoff signal amber at 10%,
                              which is a much brighter hue — so in DARK mode it
                              lifts the backdrop and takes the muted ink to
                              **3.80:1** at 12px. Measured, not reasoned: the
                              body-contrast sweep for this PR caught it, and it
                              was a regression this change introduced. The hue
                              stays on the wash and the glyph; the sentence is
                              read at the ink the rest of the panel uses. */}
                          <p className="text-xs text-fieldiq-text">{fu.ai_explanation}</p>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-fieldiq-muted">No units flagged this month.</p>
                  )}
                </div>
              </>
            ) : (
              /* Empty state — no summary for this month */
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <p className="text-sm text-fieldiq-muted">
                  No summary exists for {formatMonth(selectedMonth)}.
                </p>
                <button
                  onClick={() => generateMutation.mutate({ month: selectedMonth })}
                  disabled={generateMutation.isPending}
                  className="fi-action fi-action--standard fi-action--primary disabled:opacity-50"
                >
                  {generateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
                  {generateMutation.isPending ? 'Generating…' : 'Generate Summary'}
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Per-unit roll-up — one row per unit, due state and cost together.
            Every unit appears, including ones that have never been serviced. */}
        {/* THE PAGE'S ONE AMBER 4px LEFT EDGE. It goes on this panel, not on all
            of them: this is the register the chips above narrow. */}
        <section aria-labelledby="maintenance-units-title" className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
          <div className="flex flex-col gap-2 border-b border-fieldiq-border px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Fleet register</p>
              <h2 id="maintenance-units-title" className="mt-1 flex items-center gap-2 text-base font-extrabold tracking-tight text-fieldiq-text">
                <DollarSign className="size-4 text-fieldiq-accent" aria-hidden="true" /> Due Status &amp; Cost by Unit
              </h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={dueStatus}
                onChange={(e) => setDueStatus(e.target.value)}
                className="h-9 px-3 bg-fieldiq-dark border border-fieldiq-border rounded-fi-control text-fieldiq-text text-sm focus:outline-none focus:ring-1 focus:ring-fi-amber"
              >
                <option value="">All Due Statuses</option>
                {tiles.map((tile) => (
                  <option key={tile.status} value={tile.status}>
                    {tile.label} ({tile.count})
                  </option>
                ))}
              </select>
              <select
                value={summarySort}
                onChange={(e) => setSummarySort(e.target.value as MaintenanceSummarySort)}
                className="h-9 px-3 bg-fieldiq-dark border border-fieldiq-border rounded-fi-control text-fieldiq-text text-sm focus:outline-none focus:ring-1 focus:ring-fi-amber"
              >
                <option value="due">Sort: Most urgent</option>
                <option value="spend">Sort: Highest spend</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-xs">
              <thead>
                <tr className="border-b border-fieldiq-border">
                  <th className="text-left py-2 px-4 text-fieldiq-muted font-medium">Unit #</th>
                  <th className="text-center py-2 px-4 text-fieldiq-muted font-medium">Status</th>
                  <th className="text-left py-2 px-4 text-fieldiq-muted font-medium">Due</th>
                  <th className="text-center py-2 px-4 text-fieldiq-muted font-medium">Records</th>
                  <th className="text-right py-2 px-4 text-fieldiq-muted font-medium">Total Spend</th>
                  <th className="text-left py-2 px-4 text-fieldiq-muted font-medium">Last Service</th>
                </tr>
              </thead>
              <tbody>
                {unitSummaryLoading ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-fieldiq-muted">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto mb-1" />
                      Loading units...
                    </td>
                  </tr>
                ) : unitFailure ? (
                  /* AN ERROR IS NOT AN EMPTY STATE. "No equipment on file." —
                     and, worse, "No units are overdue." — stood here, which is a
                     screen with no data telling a shop nothing is late. */
                  <tr>
                    <td colSpan={6} className="p-0">
                      <LoadFailureNotice
                        error={queryFailureError(unitSummaryQuery)}
                        kind={unitFailure}
                        subject="the per-unit maintenance roll-up"
                      />
                    </td>
                  </tr>
                ) : unitRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-fieldiq-muted">
                      {dueStatus
                        ? `No units are ${unitStatusChip(dueStatus).label.toLowerCase()}.`
                        : 'No equipment on file.'}
                    </td>
                  </tr>
                ) : (
                  unitRows.map((u) => {
                    const chip = unitStatusChip(u.status);
                    const rule = dueRuleLabel(u);
                    return (
                      <tr
                        key={u.equipment_id}
                        className="border-b border-fieldiq-border/50 hover:bg-fieldiq-dark/50 cursor-pointer transition-colors"
                        onClick={() => setFilterUnit(u.equipment_id)}
                      >
                        <td className="py-2.5 px-4">
                          <span className="text-fieldiq-text font-medium">{unitLabel(u)}</span>
                          {u.equipment_type && (
                            <span className="block text-[11px] text-fieldiq-muted">{u.equipment_type}</span>
                          )}
                        </td>
                        <td className="py-2.5 px-4 text-center">
                          {/* The server explains itself — surface it as the chip's tooltip. */}
                          <Badge
                            variant="outline"
                            className={`${chip.cls} whitespace-nowrap`}
                            title={dueExplanation(u)}
                          >
                            {chip.label}
                          </Badge>
                        </td>
                        <td className="py-2.5 px-4 min-w-[220px]">
                          <span className="text-fieldiq-text">{unitDueText(u)}</span>
                          {rule && (
                            <span className="block text-[11px] text-fieldiq-muted">{rule}</span>
                          )}
                        </td>
                        <td className="py-2.5 px-4 text-center text-fieldiq-text">{u.record_count}</td>
                        <td className="py-2.5 px-4 text-right text-fieldiq-text font-semibold">{fmt(u.total_spend)}</td>
                        <td className="py-2.5 px-4 text-fieldiq-muted whitespace-nowrap">
                          {fmtDate(u.last_service_date)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {/* A footer earns its place by carrying a count, a SCOPE, a permission
              or a timestamp (CLAUDE.md, fe#711). This is the scope the heading
              used to hold as a chip — and it is the figure that tells a reader
              the register is narrowed, which the chips above imply but do not
              state. Absent while the read is in flight, never a zero. */}
          {!unitSummaryLoading && !unitFailure && (
            <div className="border-t border-fieldiq-border px-4 py-3 text-[11px] text-fieldiq-muted">
              {dueStatus
                ? `Showing ${unitRows.length} of ${displayValue(fleetTotal)} units.`
                : `${unitRows.length} unit${unitRows.length === 1 ? '' : 's'} on file.`}
            </div>
          )}
        </section>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={filterUnit}
            onChange={(e) => setFilterUnit(e.target.value)}
            className="h-9 px-3 bg-fieldiq-dark border border-fieldiq-border rounded-fi-control text-fieldiq-text text-sm focus:outline-none focus:ring-1 focus:ring-fi-amber"
          >
            <option value="">All Units</option>
            {(equipmentList as EquipmentUnit[]).map((eq) => (
              <option key={eq.id} value={eq.id}>
                {eq.unit_number || eq.name || eq.id}
              </option>
            ))}
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="h-9 px-3 bg-fieldiq-dark border border-fieldiq-border rounded-fi-control text-fieldiq-text text-sm focus:outline-none focus:ring-1 focus:ring-fi-amber"
          >
            <option value="">All Types</option>
            {MAINTENANCE_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="h-9 px-3 bg-fieldiq-dark border border-fieldiq-border rounded-fi-control text-fieldiq-text text-sm focus:outline-none focus:ring-1 focus:ring-fi-amber"
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s.replace('_', ' ')}</option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <label className="text-xs text-fieldiq-muted">From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-9 px-3 bg-fieldiq-dark border border-fieldiq-border rounded-fi-control text-fieldiq-text text-sm focus:outline-none focus:ring-1 focus:ring-fi-amber"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-fieldiq-muted">To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-9 px-3 bg-fieldiq-dark border border-fieldiq-border rounded-fi-control text-fieldiq-text text-sm focus:outline-none focus:ring-1 focus:ring-fi-amber"
            />
          </div>
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fieldiq-muted" />
            <input
              placeholder="Search records..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-9 pl-9 pr-3 bg-fieldiq-dark border border-fieldiq-border rounded-fi-control text-fieldiq-text text-sm placeholder-fieldiq-muted focus:outline-none focus:ring-1 focus:ring-fi-amber"
            />
          </div>
          {/* The export, beside the filters that scope it. Same handler, same
              params, same spinner — see the note on the band's actions. */}
          <button
            type="button"
            onClick={handleDownloadHistoryPdf}
            disabled={downloadingHistoryPdf}
            className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 text-sm font-semibold text-fieldiq-text transition hover:bg-fieldiq-dark/50 disabled:opacity-50"
          >
            {downloadingHistoryPdf
              ? <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              : <Download className="size-4" aria-hidden="true" />}
            Download History PDF
          </button>
        </div>

        {/* Maintenance history table */}
        <section aria-labelledby="maintenance-history-title" className="overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
          <div className="border-b border-fieldiq-border px-4 py-4">
            <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Records</p>
            <h2 id="maintenance-history-title" className="mt-1 flex items-center gap-2 text-base font-extrabold tracking-tight text-fieldiq-text">
              <Wrench className="size-4 text-fieldiq-accent" aria-hidden="true" /> Maintenance History
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-xs">
              <thead>
                <tr className="border-b border-fieldiq-border">
                  <th className="text-left py-2 px-4 text-fieldiq-muted font-medium whitespace-nowrap">Date</th>
                  <th className="text-left py-2 px-4 text-fieldiq-muted font-medium whitespace-nowrap">Unit #</th>
                  <th className="text-left py-2 px-4 text-fieldiq-muted font-medium whitespace-nowrap">Type</th>
                  <th className="text-left py-2 px-4 text-fieldiq-muted font-medium">Description</th>
                  <th className="text-left py-2 px-4 text-fieldiq-muted font-medium whitespace-nowrap">Performed by / Vendor</th>
                  <th className="text-right py-2 px-4 text-fieldiq-muted font-medium whitespace-nowrap">Total Cost</th>
                  <th className="text-center py-2 px-4 text-fieldiq-muted font-medium whitespace-nowrap">Status</th>
                  <th className="w-10 py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {recordsLoading ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-fieldiq-muted">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto mb-1" />
                      Loading records...
                    </td>
                  </tr>
                ) : recordsFailure ? (
                  /* AN ERROR IS NOT AN EMPTY STATE: "No maintenance records
                     found." read as a clean service history. */
                  <tr>
                    <td colSpan={8} className="p-0">
                      <LoadFailureNotice
                        error={queryFailureError(recordsQuery)}
                        kind={recordsFailure}
                        subject="maintenance records"
                      />
                    </td>
                  </tr>
                ) : filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-fieldiq-muted">
                      No maintenance records found.
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map((rec) => (
                    <tr
                      key={rec.id}
                      className="border-b border-fieldiq-border/50 hover:bg-fieldiq-dark/50 cursor-pointer transition-colors"
                      onClick={() => openEdit(rec)}
                    >
                      <td className="py-2.5 px-4 text-fieldiq-muted whitespace-nowrap">{fmtDate(rec.service_date)}</td>
                      <td className="py-2.5 px-4 text-fieldiq-text font-medium whitespace-nowrap">{rec.unit_number || '—'}</td>
                      <td className="py-2.5 px-4 text-fieldiq-text whitespace-nowrap">{rec.maintenance_type || '—'}</td>
                      <td className="py-2.5 px-4 text-fieldiq-text max-w-[200px] truncate">{rec.description || '—'}</td>
                      <td className="py-2.5 px-4 text-fieldiq-muted whitespace-nowrap">
                        {rec.performed_by || rec.vendor || '—'}
                      </td>
                      <td className="py-2.5 px-4 text-right text-fieldiq-text font-semibold whitespace-nowrap">{fmt(rec.total_cost)}</td>
                      <td className="py-2.5 px-4 text-center">{statusBadge(rec.status)}</td>
                      <td className="py-2.5 px-2 text-center">
                        <Tooltip content="Download PDF">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDownloadRecordPdf(rec.id, rec.unit_number); }}
                            disabled={downloadingRecordPdf === rec.id}
                            className="p-1.5 rounded hover:bg-fieldiq-dark/50 text-fieldiq-muted hover:text-fieldiq-text transition-colors disabled:opacity-50"
                            aria-label="Download PDF"
                          >
                            {downloadingRecordPdf === rec.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                          </button>
                        </Tooltip>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* A COUNT, and a SCOPE when the search narrows the loaded rows. */}
          {!recordsLoading && !recordsFailure && (
            <div className="border-t border-fieldiq-border px-4 py-3 text-[11px] text-fieldiq-muted">
              {search
                ? `Showing ${filteredRecords.length} of ${(records as MaintenanceRecord[]).length} loaded records.`
                : `${filteredRecords.length} record${filteredRecords.length === 1 ? '' : 's'} in this date range.`}
            </div>
          )}
        </section>
        </div>
      </section>

      {/* ── Add/Edit Dialog ── */}
      <MaintenanceRecordDialog
        open={dialogOpen}
        onClose={closeDialog}
        record={editingRecord}
        renderFooterExtra={(rec) => (
          <>
            <button
              onClick={() => { closeDialog(); setDeleteTarget(rec); }}
              /* LEFT AS IT WAS, AND REPORTED. `text-red-400` is a bright
                 dark-mode hue with no light remap, so this reads low on the
                 dialog's white surface — the same defect as Financial Summary's
                 emerald/amber inks, which this PR did fix. It is NOT fixed here
                 because neither candidate is right without an operator's call:
                 `text-fieldiq-danger` measures 4.43 on white (under the floor,
                 computed longhand), and the theme's own ink drops the only
                 destructive affordance this control has. This sits in the
                 dialog's footer rather than the page body besides. */
              className="text-red-400 text-sm font-medium hover:text-red-300 transition-colors"
            >
              Delete Record
            </button>
            <button
              onClick={() => handleDownloadRecordPdf(rec.id, rec.unit_number)}
              disabled={downloadingRecordPdf === rec.id}
              className="flex items-center gap-1.5 bg-fieldiq-card border border-fieldiq-border text-fieldiq-muted px-3 py-2 rounded-fi-control text-sm font-medium hover:text-fieldiq-text transition-colors disabled:opacity-50"
            >
              {downloadingRecordPdf === rec.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download PDF
            </button>
          </>
        )}
      />

      {/* ── Delete Confirmation ── */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Maintenance Record"
        body={
          <p>
            Are you sure you want to delete the maintenance record for{' '}
            <strong>{deleteTarget?.unit_number}</strong> on{' '}
            {fmtDate(deleteTarget?.service_date)}? This action cannot be undone.
          </p>
        }
        confirmLabel="Delete"
        confirmVariant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
        }}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* ── Regenerate Confirmation ── */}
      <ConfirmModal
        isOpen={regenerateConfirmOpen}
        title="Regenerate Summary"
        body={
          <p>
            This will overwrite the existing summary for{' '}
            <strong>{formatMonth(selectedMonth)}</strong>. Any status changes
            (reviewed / published) will be reset to draft.
          </p>
        }
        confirmLabel="Regenerate"
        confirmVariant="primary"
        loading={generateMutation.isPending}
        onConfirm={() => generateMutation.mutate({ month: selectedMonth, force: true })}
        onCancel={() => setRegenerateConfirmOpen(false)}
      />
    </div>
  );
}
